/**
 * @file payment-completed.tsx
 * @deprecated Copied to app/modules/procurement/pages/vendors_management/payments/payment-completed.tsx. This file still serves the existing /UProcure routes.
 * @module app/modules/uProcure/vendors_management/payments/payment-completed
 * @description Page component for managing payment completed in the vendors management section.
 *   Displays a list of payment completed with pagination, search functionality, and CRUD operations.
 *   Allows users to view the details of payment completed with warehouse context integration.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 *
 * @todo Permission mismatch: uses PaymentToSellers (module 5) but uTrade vendors payments use
 *   PermissionModule.PaymentToVendors (75) in payments-to-vendors and vendor-finance-details.
 * @todo Remove debug console.log calls (state, localStorage businessId, selected).
 */

import { Button, Card, Col, Row } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import VerifiedPro from "../../../../../_admin/assets/media/svg_uTrade/verified.svg";
import {
  Accepted,
  All,
  AllSeller,
  NewRequests,
  OrdersDelivery,
  PAGE_LIMIT,
  PaymentToSellers,
  RefundsToCustomer,
  Rejected,
  BankPayment,
  Credit,
  TigoPesaPayment,
  UnderReview,
  View,
} from "../../../../../utils/constants";
import APICallService from "../../../../../api/apiCallService";
import { seller, vendor } from "../../../../../api/apiEndPoints";
import { poQuantityText } from "../../vendor-ledger/supplier-ledger-groups";
import Method from "../../../../../utils/methods";
import { useAuth } from "../../../auth";
import PermissionModal from "../../../../modals/permission";
import { useWarehouse } from "../../../../context/WarehouseContext";
import { ColumnDef } from "@tanstack/react-table";
import { CustomTable } from "../../../../custom/data-table";
import { useNavigationHistory } from "../../../../../utils/useNavigationHistory";
const PaymentCompleted = () => {
  const { selectedWarehouse } = useWarehouse();
  const { currentUser } = useAuth();

    const { navigateTo } = useNavigationHistory();
  
  const { state }: any = useLocation();
  const [details, setDetails] = useState<any>();
  const [fetchLoading, setFetchLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(PAGE_LIMIT);
  const [search, setSearch] = useState<string>("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [paymentData, setPaymentData] = useState<any>([]);
  const [transferData, setTransferData] = useState<any>([]);
  const [paymentDetails, setPaymentDetails] = useState<any>([]);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [showOrderPermissionModal, setShowOrderPermissionModal] =
    useState(false);
  console.log("state", state);
  useEffect(() => {
    (async () => {
      setFetchLoading(true);
      await setDetails(state.businessDetails);
      await setPaymentDetails(state.paymentVal);
      await fetchOrder(page, pageLimit, state.paymentVal._id, search);
      setFetchLoading(false);
    })();
  }, []);
  const fetchOrder = async (
    pageNo: number,
    limit: number,
    businessId: string,
    searchValue: string
  ) => {
    let params = {
      pageNo: pageNo,
      limit: limit,
      sortKey: "_createdAt",
      sortOrder: -1,
      needCount: pageNo === 1,
      searchTerm: searchValue,
    };
    let apiService = new APICallService(
      vendor.payoutInfo,
      businessId,
      "",
      "",
      "",
      "",
      PaymentToSellers,
      selectedWarehouse?.value
    );
    let response = await apiService.callAPI();
    if (response) {
      if (pageNo === 1) {
        setTotalRecords(response.total);
      } else {
        let prevTotal = totalRecords;
        setTotalRecords(prevTotal);
      }
      setPaymentData(response.orders);
      setTransferData(response?.transferRequest);
    }
  };

  const canRowClick =
    Method.hasPermission(PaymentToSellers, View, currentUser) &&
    Method.hasPermission(OrdersDelivery, View, currentUser);

  const goToOrderDetails = (paymentVal: any) => {
    navigateTo("/UProcure/purchase-order-management/purchase-order-details", {
      state: {
        id: paymentVal?._id,
        moduleName: PaymentToSellers,
      },
    });
  };

  const columns: ColumnDef<any>[] = [
    {
      header: "Date & ID",
      cell: ({ row }) => (
        <>
          <span className="text-dark d-block">
            {Method.convertDateToDDMMYYYYHHMM(row.original?._createdAt, "-")}
          </span>
          <span className="text-gray d-block">{row.original?.refKey}</span>
        </>
      ),
    },
    {
      header: "Supplier name",
      cell: ({ row }) => row.original?.business?.ownerName || "-",
    },
    {
      header: "Quantity",
      // each quantity with its unit; quantities of different units are never added up
      cell: ({ row }) => poQuantityText(row.original || {}),
    },
    {
      header: "Total amount",
      cell: ({ row }) =>
        `TSh ${Method.getGeneralizedAmount(row.original?.charge || 0)}`,
    },
  ];

  return (
    <>
      {showPermissionModal && (
        <PermissionModal
          show={showPermissionModal}
          onHide={() => setShowPermissionModal(false)}
          moduleName={"seller"}
        />
      )}
      {showOrderPermissionModal && (
        <PermissionModal
          show={showOrderPermissionModal}
          onHide={() => setShowOrderPermissionModal(false)}
          moduleName={"orders & delivery"}
        />
      )}

      <div className="p-5">
        {Object.keys(paymentDetails).length ? (
          <>
            <Row className="g-4">
              <Col md>
                <h1 className="fs-22 fw-bolder">
                  Payment completed of {paymentDetails.totalOrders} orders on{" "}
                  {Method.convertDateToDDMMYYYYHHMM(
                    paymentDetails._createdAt,
                    "-"
                  )}
                </h1>
              </Col>
              {details && Object.keys(details).length ? (
                <>
                  <Card className="border-r10px mt-7">
                    <Card.Body className="p-7">
                      <Row className="align-items-center g-5">
                        <Col xs="auto">
                          <div className="symbol symbol-50px border">
                            <img
                              src={details.image}
                              className="img-fluid object-fit-contain"
                              alt=""
                            />
                          </div>
                        </Col>
                        <Col xs className="ps-lg-0">
                          <div className="fs-20 fw-bolder">
                            {details.ownerName}
                            {details.KYC.status === Accepted - 1 ? (
                              <img
                                className="ms-2 mb-1"
                                width={24}
                                height={24}
                                src={VerifiedPro}
                                alt={details.name}
                              />
                            ) : (
                              <></>
                            )}
                            {details.KYC.status === All - 1 ||
                              details.KYC.status === NewRequests - 1 ||
                              details.KYC.status === UnderReview - 1 ? (
                              <>
                                <span className="bage badge-light-warning text-dark border-r5px fs-14 fw-600 ms-2 p-2">
                                  Verification pending
                                </span>
                              </>
                            ) : (
                              <></>
                            )}
                            {details.KYC.status === Rejected - 1 ? (
                              <>
                                <span className="bage badge-light-danger text-dark border-r5px fs-14 fw-600 ms-2 p-2">
                                  Rejected
                                </span>
                              </>
                            ) : (
                              <></>
                            )}
                          </div>
                        </Col>
                        <Col sm="auto" className="text-end">
                          {Method.hasPermission(
                            PaymentToSellers,
                            View,
                            currentUser
                          ) && Method.hasPermission(AllSeller, View, currentUser) ? (
                            <Button
                              variant="link"
                              className="btn-flush fs-16 fw-bold text-primary"
                              onClick={() => {
                                  navigateTo("/UProcure/vendors/vendor-profile", {
                                  state: {
                                    id: details.id,
                                    moduleName: PaymentToSellers,
                                  },
                                });
                              }}
                            >
                              View vendor profile
                            </Button>
                          ) : (
                            <></>
                          )}
                          {Method.hasPermission(
                            PaymentToSellers,
                            View,
                            currentUser
                          ) && !Method.hasPermission(AllSeller, View, currentUser) ? (
                            <Button
                              variant="link"
                              className="btn-flush fs-16 fw-bold text-primary"
                              onClick={() => {
                                setShowPermissionModal(true);
                              }}
                            >
                              View seller profile
                            </Button>
                          ) : (
                            <></>
                          )}
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </>
              ) : (
                <></>
              )}
              <Col md="auto">
                <span className="fs-18 fw-600">
                  Total paid: TSh{" "}
                  {Method.getGeneralizedAmount(paymentDetails.totalAmount)}
                </span>
              </Col>
            </Row>
            <Row className="mt-5 g-5">
              {paymentDetails?.completedBy?.user?.name && (
                <Col md="auto">
                  <label className="fs-6 fw-bold text-gray-600">
                    Payment done by
                  </label>
                  <div className="fs-5 fw-bolder text-dark">
                    {paymentDetails?.completedBy?.user?.name}
                  </div>
                </Col>
              )}
              {paymentDetails?.paymentMethod && (
                <Col md="auto">
                  <label className="fs-6 fw-bold text-gray-600">
                    Payment Method
                  </label>
                  <div className="fs-5 fw-bolder text-dark">
                    {paymentDetails?.paymentMethod === BankPayment
                      ? "Bank Transfer"
                      : paymentDetails?.paymentMethod === Credit
                        ? "Credit"
                        : paymentDetails?.paymentMethod === TigoPesaPayment
                          ? "Tigo Pesa"
                          : "-"}
                  </div>
                </Col>
              )}
              {paymentDetails?.paymentRef && (
                <Col md="auto">
                  <label className="fs-6 fw-bold text-gray-600">Ref ID</label>
                  <div className="fs-5 fw-bolder text-dark">
                    {paymentDetails?.paymentRef}
                  </div>
                </Col>
              )}
            </Row>

            {paymentDetails?.paymentMethod === BankPayment &&
              paymentDetails?.bankDetails && (
                <div className="mt-5 p-5 border border-dashed border-gray-300 rounded">
                  <h3 className="fs-18 fw-bolder text-dark mb-4">
                    Bank Details
                  </h3>
                  <Row className="g-5">
                    <Col md="auto">
                      <label className="fs-7 fw-bold text-gray-600">
                        Bank Name
                      </label>
                      <div className="fs-6 fw-bolder text-dark">
                        {paymentDetails.bankDetails.bankName}
                      </div>
                    </Col>
                    <Col md="auto">
                      <label className="fs-7 fw-bold text-gray-600">
                        Branch Name
                      </label>
                      <div className="fs-6 fw-bolder text-dark">
                        {paymentDetails.bankDetails.branchName}
                      </div>
                    </Col>
                    <Col md="auto">
                      <label className="fs-7 fw-bold text-gray-600">
                        Account Number
                      </label>
                      <div className="fs-6 fw-bolder text-dark">
                        {paymentDetails.bankDetails.accountNumber}
                      </div>
                    </Col>
                    {paymentDetails.bankDetails.identifierCode && (
                      <Col md="auto">
                        <label className="fs-7 fw-bold text-gray-600">
                          Identifier Code
                        </label>
                        <div className="fs-6 fw-bolder text-dark">
                          {paymentDetails.bankDetails.identifierCode}
                        </div>
                      </Col>
                    )}
                  </Row>
                </div>
              )}
          </>
        ) : (
          <>
            <Row className="g-4">
              <Col md>
                <h1 className="fs-22 fw-bolder">Payment completed</h1>
              </Col>
            </Row>
          </>
        )}
        {/* <Card className="border-r10px mt-5">
          <Card.Body className="p-7">
            <Row className="align-items-center g-5">
              <Col xs="auto">
                <div className="symbol symbol-50px border">
                  <img
                    src={IMAGES.WadsworthDistributorsLtd}
                    className="img-fluid object-fit-contain"
                    alt=""
                  />
                </div>
              </Col>
              <Col
                xs
                className="ps-lg-0"
              >
                <div className="fs-20 fw-bolder">
                  Wadsworth Distributors Limited
                  <img
                    className="ms-2 mb-1"
                    width={24}
                    height={24}
                    src={IMAGES.verifiedBadge}
                    alt=""
                  />
                </div>
              </Col>
              <Col
                sm="auto"
                className="text-end"
              >
                <Button
                  variant="link"
                  className="btn-flush fs-16 fw-bold text-primary"
                >
                  View seller profile
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card> */}

        {state?.moduleName == "order" && (
          <Card className="border border-r10px mt-6">
            <Card.Body className="p-0">
              <CustomTable
                data={paymentData}
                columns={columns}
                loading={fetchLoading}
                onRowClick={canRowClick ? goToOrderDetails : undefined}
              />
            </Card.Body>
          </Card>
        )}

      </div >
    </>
  );
};
export default PaymentCompleted;
